import React, {useCallback, useEffect, useRef, useState} from 'react'
import Button from "./Button";
import PropTypes from "prop-types";
import Input from "./Input";
import {useForm} from "../../hooks/form/formHook";
import DateTimePicker from "./DateTimePicker";
import Select from "./Select";
import {useTranslation} from "react-i18next";
import {get, isArray, isEmpty} from "lodash";
import Fragment from "./Fragment";
import Checkbox from "./Checkbox";
import Currency from "./Currency";
import {closestParentByClass} from "../../utils/DomUtils";
import useUpdateOnlyEffect from "../../hooks/effect/useUpdateOnlyEffect";
import * as DateUtils from "../../utils/DateUtils";

const TableFilter = (props) => {
    const {t} = useTranslation();
    const [filterOpen, setFilterOpen] = useState(false);
    const [filter, setFilter] = useState([]);

    const drawerElement = useRef({
        querySelector: () => {
        }
    });

    const {values, setValue, setFormNotDirty, getField} = useForm();

    const onOutsideFilterClick = useCallback((event) => {
        if (drawerElement.current.classList.contains("open") && closestParentByClass("table-filter", event.target) === null) {
            setFilterOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("click", onOutsideFilterClick);
        return () => document.removeEventListener("click", onOutsideFilterClick);
    }, []);

    useEffect(() => {
        // autofocus first input in the drawer
        filterOpen && drawerElement.current.querySelector(".first-filter .field:not(.dropdown) input")?.focus();
    }, [filterOpen]);

    useUpdateOnlyEffect(() => {
        const filters = props.columns.reduce((filter, column) => {
            const value = get(values, column.id);
            let from = value?.from;
            let to = value?.to;

            if (!Number.isInteger(value) && typeof value !== "boolean" && isEmpty(value)) { //isEmpty is returning true for integers and booleans
                return filter;
            }

            switch (column.type) {
                case "string":
                case "numericString":
                    if (isArray(value)) {
                        const parts = [...(value
                            .map(part => ({
                                name: column.id,
                                operator: "LIKE_IC",
                                value: part[column.id]
                            })))];
                        filter.push({
                            name: column.id,
                            type: "OR",
                            filters: parts
                        });
                    } else {
                        filter.push({
                            name: column.id,
                            operator: "LIKE_IC",
                            value: value,
                        });
                    }
                    break;
                case "select":
                case "findAndSelect":
                    if (String(value) === "null") {
                        filter.push({
                            name: column.id,
                            operator: "ISNULL"
                        });
                        break;
                    }
                    if (isArray(value)) {
                        const parts = [...(value
                            .filter(part => part !== "null" && part !== null)
                            .map(part => ({
                                name: column.id,
                                operator: "EQ",
                                value: part
                            })))];
                        if (parts && !parts.isEmpty) filter.push({
                            name: column.id,
                            type: "OR",
                            filters: parts
                        });
                    } else {
                        value != null && value != "" && filter.push({
                            name: column.id,
                            operator: "EQ",
                            value: value
                        });
                    }
                    filter.push();
                    break;
                case "date":
                    if (isEmpty(from) && isEmpty(to)) {
                        break;
                    }
                    if (!isEmpty(from)) {
                        filter.push({
                            name: column.id,
                            operator: "GT",
                            value: DateUtils.subtractDays(from, 0)
                        });
                    }
                    if (!isEmpty(to)) {
                        filter.push({
                            name: column.id,
                            operator: "LT",
                            value: DateUtils.addDays(from, 1)
                        });
                    }
                    break;
                case "datetime":
                    if (isEmpty(from) && isEmpty(to)) {
                        break;
                    }

                    if (!isEmpty(from)) {
                        filter.push({
                            name: column.id,
                            operator: "GTE",
                            value: from
                        });
                    }
                    if (!isEmpty(to)) {
                        filter.push({
                            name: column.id,
                            operator: "LTE",
                            value: to
                        });
                    }
                    break;
                case "number":
                case "currency":
                    if (from == null && to == null) {
                        break;
                    }

                    parseFloat(from) > parseFloat(to) && ([to, from] = [from, to]); //reverse variable values
                    if (from != null) {
                        filter.push({
                            name: column.id,
                            operator: "GTE",
                            value: from
                        });
                    }
                    if (to != null) {
                        filter.push({
                            name: column.id,
                            operator: "LTE",
                            value: to
                        });
                    }
                    break;
                case "boolean":
                    filter.push({
                        name: column.id,
                        operator: "EQ",
                        value: String(value)
                    })
                    break;
                default:
                    return {};
            }
            return filter;
        }, []);

        setFilter(filters);

        props.onFilter && props.onFilter(filters);
    }, [values]);

    useEffect(() => {
        // only update filter when existing filter is empty (this means we are returned to the page)
        if (props.filter && props.filter.length > 0 && filter.length === 0) {
            const columnMap = props.columns.reduce((map, column) => {
                map[column.id] = column;
                return map;
            }, {});

            props.filter.forEach(filter => {
                const column = columnMap[filter.name];
                let value;
                if (!column) {
                    return;
                }

                switch (column.type) {
                    case "string":
                    case "numericString":
                        value = filter.value.replace(/^'+|'+$/gm, "").replace(/\\'/gm, "'");
                        values[column.id] !== value && setValue(column.id, value);
                        break;
                    case "select":
                    case "findAndSelect":
                        filter.operator === "EQ" && setValue(column.id, filter.value);
                        break;
                    case "date":
                    case "datetime":
                        if (filter.operator === "GTE") {
                            setValue(column.id + ".from", filter.value);
                        } else if (filter.operator === "LTE") {
                            setValue(column.id + ".to", filter.value);
                        }
                        break;
                    case "number":
                    case "currency":
                        if (filter.operator === "GTE") {
                            setValue(column.id + ".from", filter.value);
                        } else if (filter.operator === "LTE") {
                            setValue(column.id + ".to", filter.value);
                        }
                        break;
                    case "boolean":
                        filter.operator === "EQ" && setValue(column.id, filter.value === "true");
                        break;
                    default:
                        return {};
                }
            });
        }
    }, [props.filter]);

    const exportData = (filter) => {
        props.onExport && props.onExport(filter);
    }

    return (
        <div className="table-filter ">
            <Fragment rendered={!!props.onExport}>
                <Button className="fz fz-file-export large"
                        title={props.title || t("Filter")}
                        isOutlined={true}
                        isLoading={props.isExporting}
                        onClick={() => exportData(filter)}
                />
            </Fragment>
            <div className="is-relative is-inline-flex">
                <Button className="fz fz-filter1 large marginless filter-button"
                        color={props.filter.length > 0 ? "primary" : "default"}
                        isOutlined={true} disabled={props.disabled} onClick={() => setFilterOpen(!filterOpen)}/>
            </div>
            <div ref={drawerElement}
                 className={filterOpen ? "table-filter-drawer card open" : "table-filter-drawer card"}>
                <div className="card-header-title">
                    <span className="flex-grow-1 is-size-4">{props.title || t("Filter")}</span>
                    <Button className="fz fz-times marginless" color="danger" onClick={() => setFilterOpen(false)}
                            isOutlined={true}/>
                </div>
                <div className="card-content">
                    {props.columns.map((column, index) =>
                        <div key={column.id} className={index === 0 ? "columns first-filter" : "columns"}>
                            {
                                column.type === "string" &&
                                <div className="column">
                                    <Input label={t(column.name)} regex={"[^&'\"]+"} formField={getField(column.id)}/>
                                </div>

                            }
                            {
                                column.type === "numericString" &&
                                <div className="column">
                                    <Input label={t(column.name)} regex={"[^&'\"]+"} formField={getField(column.id)}
                                           type="number"/>
                                </div>
                            }
                            {
                                column.type === "date" &&
                                <React.Fragment>
                                    <div className="column">
                                        <DateTimePicker label={t(column.name + "_from")}

                                                        formField={getField(column.id + ".from")}/>
                                    </div>
                                    <div className="column">
                                        <DateTimePicker label={t(column.name + "_to")}
                                                        formField={getField(column.id + ".to")}/>
                                    </div>
                                </React.Fragment>
                            }
                            {
                                column.type === "datetime" &&
                                <React.Fragment>
                                    <div className="column">
                                        <DateTimePicker label={t(column.name + "_from")}
                                                        formField={getField(column.id + ".from")} type="datetime"/>
                                    </div>
                                    <div className="column">
                                        <DateTimePicker label={t(column.name + "_to")}
                                                        formField={getField(column.id + ".to")} type="datetime"/>
                                    </div>
                                </React.Fragment>
                            }
                            {
                                column.type === "currency" &&
                                <React.Fragment>
                                    <div className="column">
                                        <Currency label={t(column.name + "_from")}
                                                  formField={getField(column.id + ".from")}/>
                                    </div>
                                    <div className="column">
                                        <Currency label={t(column.name + "_to")}
                                                  formField={getField(column.id + ".to")}/>
                                    </div>
                                </React.Fragment>
                            }
                            {
                                column.type === "number" &&
                                <React.Fragment>
                                    <div className="column">
                                        <Input type="number" label={t(column.name + "_from")}
                                               formField={getField(column.id + ".from")}/>
                                    </div>
                                    <div className="column">
                                        <Input type="number" label={t(column.name + "_to")}
                                               formField={getField(column.id + ".to")}/>
                                    </div>
                                </React.Fragment>
                            }

                            {
                                column.type === "select" &&
                                <div className="column">
                                    <Select label={t(column.name)}
                                            formField={getField(column.id)}
                                            options={column.options}
                                            optionsCode={column.optionsCode}
                                            optionsValue={column.optionsValue}
                                            optionsLabel={column.optionsLabel}
                                            isMultiSelect={column.isMultiSelect}

                                            emptyOption={true}/>
                                </div>
                            }
                            {
                                column.type === "findAndSelect" &&
                                <div className="column">
                                    <Select label={t(column.name)}
                                            isMultiSelect={column.isMultiSelect}
                                            formField={getField(column.id)}
                                            onSearchQueryChange={(change) => {
                                                column.onSearchQueryChange(change);
                                            }}
                                            loading={column.loading}
                                            fetchMoreOptions={column.fetchMoreOptions}
                                            hasMoreOptions={column.hasMoreOptions}
                                            options={column.options}
                                            optionsCode={column.optionsCode}
                                            optionsValue={column.optionsValue}
                                            optionsLabel={column.optionsLabel}
                                            emptyOption={false}/>
                                </div>
                            }
                            {
                                column.type === "boolean" &&
                                <div className="column">
                                    <Checkbox label={t(column.name)} formField={getField(column.id)} type="form"
                                              color="auto" threeStateMode={true}/>
                                </div>
                            }
                        </div>
                    )}
                    <Button className="fz fz-trash-alt" label={t("remove_filters")} color="danger" isOutlined={true}
                            onClick={() => setFormNotDirty(true)} disabled={false}/>
                </div>
            </div>
        </div>
    );
}

TableFilter.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            type: PropTypes.oneOf(["string", "numericString", "select", "date", "datetime", "currency", "number", "boolean", "findAndSelect"]).isRequired,
            name: PropTypes.string.isRequired,
            options: PropTypes.array, // For select type
            optionsLabel: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
            optionsValue: PropTypes.string, // For select type
            optionsCode: PropTypes.string, // For select type
            isMultiSelect: PropTypes.bool,
            onSearchQueryChange: PropTypes.func, // For custom type
            nullable: PropTypes.bool, // For select type
            customComponents: PropTypes.arrayOf(PropTypes.elementType), // New property for an array of custom components
            loading: PropTypes.bool,
            fetchMoreOptions: PropTypes.func,
            hasMoreOptions: PropTypes.bool
        })
    ).isRequired,
    filter: PropTypes.array.isRequired,
    onFilter: PropTypes.func.isRequired,
    useTableHandle: PropTypes.object,
    onExport: PropTypes.func,
    isExporting: PropTypes.bool,
    disabled: PropTypes.bool,
    title: PropTypes.string,
    sortControls: PropTypes.shape
    ({
        sortColumns: PropTypes.array.isRequired,
        sortObj: PropTypes.object.isRequired,
        onSortChange: PropTypes.func.isRequired
    })

};


export default TableFilter
